import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowRight, Phone, MessagesSquare, Workflow, CalendarCheck, Cpu, GitBranch } from "lucide-react";
import AiChatDemo from "@/components/marketing/AiChatDemo";
import OrderAiSystemModal from "@/components/marketing/OrderAiSystemModal";

const ICONS = { "voice-agent": Phone, "chat-agent": MessagesSquare, "whatsapp": MessagesSquare, "appointment": CalendarCheck, "omnichannel": Workflow, "crm-sync": GitBranch };

export default function AISystems() {
  const [items, setItems] = useState([]);
  const [orderOpen, setOrderOpen] = useState(false);
  const [activeSystem, setActiveSystem] = useState(null);
  useEffect(() => { api.get("/catalog/ai-systems").then(r => setItems(r.data)); }, []);

  const openOrder = (sys) => {
    setActiveSystem(sys);
    setOrderOpen(true);
  };

  return (
    <div>
      <section className="relative border-b border-slate-200 overflow-hidden">
        <div className="absolute inset-0 dot-grid dot-grid-fade opacity-70" />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20">
          <p className="text-xs font-semibold tracking-[0.2em] uppercase text-primary">AI systems</p>
          <h1 className="mt-3 text-5xl sm:text-6xl tracking-[-0.04em] font-semibold text-slate-900 max-w-3xl">AI systems built for financial operations.</h1>
          <p className="mt-5 max-w-2xl text-lg text-slate-600">Sub-300ms voice, FCA-compliant scripts, calendar-aware booking and CRM-native handoffs — operated by Connexis, embedded in your routing.</p>
        </div>
      </section>

      <section className="py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {items.map((a) => {
            const Icon = ICONS[a.slug] || Cpu;
            return (
              <div key={a.slug} className="group relative overflow-hidden rounded-2xl border border-slate-200 bg-white p-7 tilt-hover hover:shadow-float" data-testid={`ai-card-${a.slug}`}>
                <div className="flex items-center justify-between">
                  <span className="inline-grid place-items-center size-10 rounded-lg bg-slate-900 text-white"><Icon className="size-5" strokeWidth={1.6} /></span>
                  <Badge variant="outline" className="text-[10px] tracking-wider uppercase">{a.tag}</Badge>
                </div>
                <h3 className="mt-5 text-xl font-medium text-slate-900 tracking-tight">{a.name}</h3>
                <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">{a.desc}</p>
                <div className="mt-6 pt-5 border-t border-slate-200 flex items-end justify-between">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-500">Setup</div>
                    <div className="text-lg font-semibold text-slate-900 tabular-nums">£{a.setup.toLocaleString()}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-[10px] uppercase tracking-widest text-slate-500">Monthly</div>
                    <div className="text-lg font-semibold text-slate-900 tabular-nums">£{a.monthly.toLocaleString()}</div>
                  </div>
                </div>
                <Button onClick={() => openOrder(a)} className="mt-5 w-full rounded-full bg-[#0055FF] hover:bg-[#0044CC]" data-testid={`ai-order-${a.slug}`}>
                  Order this system <ArrowRight className="size-4" />
                </Button>
              </div>
            );
          })}
        </div>
      </section>

      <section className="py-20 bg-slate-50/60 border-y border-slate-200">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-start">
          <div>
            <p className="text-xs font-semibold tracking-[0.2em] uppercase text-primary">Try it live</p>
            <h2 className="mt-3 text-3xl sm:text-4xl tracking-[-0.02em] font-medium text-slate-900">Talk to Connexis AI. Right now.</h2>
            <p className="mt-3 text-slate-600">A live, FCA-aware Connexis AI agent qualifying you the way it would qualify your prospects. Pick a sector, ask anything — see how it captures intent, affordability and consent in seconds.</p>
            <ul className="mt-6 space-y-2 text-sm text-slate-700">
              <li className="flex items-center gap-2"><span className="size-1.5 rounded-full bg-emerald-500" /> Sub-second responses, British English</li>
              <li className="flex items-center gap-2"><span className="size-1.5 rounded-full bg-emerald-500" /> Steered to compliance — no regulated advice</li>
              <li className="flex items-center gap-2"><span className="size-1.5 rounded-full bg-emerald-500" /> Same script architecture as our production voice agent</li>
            </ul>
            <div className="mt-8 flex gap-3">
              <Link to="/book-demo"><Button className="rounded-full px-5" data-testid="ai-talk-sales">Talk to engineering <ArrowRight className="size-4" /></Button></Link>
            </div>
          </div>
          <AiChatDemo />
        </div>
      </section>

      <section className="py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="text-3xl sm:text-4xl tracking-[-0.02em] font-medium text-slate-900">Deployed in days, not quarters.</h2>
            <p className="mt-3 text-slate-600">Our onboarding team writes your compliance script, integrates your CRM, configures routing and goes live — typically inside 14 days.</p>
            <ol className="mt-8 space-y-4">
              {[
                ["01", "Discovery call", "Goals, sectors, CRM stack, compliance scope."],
                ["02", "Script + flow design", "FCA-aware script + objection handling."],
                ["03", "Sandbox + integration", "Test calls, webhooks, calendar sync."],
                ["04", "Go-live + tune", "Daily tuning in week one, weekly thereafter."],
              ].map(([n, t, d]) => (
                <li key={n} className="flex gap-4">
                  <span className="shrink-0 size-9 grid place-items-center rounded-md bg-slate-900 text-white text-xs font-mono">{n}</span>
                  <div><div className="font-medium text-slate-900">{t}</div><div className="text-sm text-slate-600">{d}</div></div>
                </li>
              ))}
            </ol>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white shadow-float p-6">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold tracking-widest uppercase text-slate-500">Booked appointment</span>
              <span className="inline-flex items-center gap-1.5 text-xs text-emerald-700"><span className="size-1.5 rounded-full bg-emerald-500 animate-pulse" /> Confirmed</span>
            </div>
            <div className="mt-4 rounded-xl bg-slate-50 border border-slate-200 p-4">
              <div className="text-sm text-slate-500">Connexis × Northbridge Mortgage Co.</div>
              <div className="mt-1 text-2xl font-semibold tracking-tight text-slate-900">Mr. James Patel — Remortgage</div>
              <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
                <Info l="Date" v="Tue, 18 Feb 2026" />
                <Info l="Time" v="11:15 GMT" />
                <Info l="Property value" v="£428,000" />
                <Info l="Outstanding" v="£218,000" />
              </div>
              <div className="mt-4 inline-flex items-center gap-2 text-xs text-slate-500">
                Pre-qualified by Connexis Voice · Recording on file · Consent captured
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 text-center">
        <div className="mx-auto max-w-3xl px-6">
          <h3 className="text-3xl sm:text-4xl tracking-[-0.02em] font-medium text-slate-900">See an AI agent built for your sector.</h3>
          <p className="mt-3 text-slate-600">Bring your script — we'll show you the voice + chat flow live, end-to-end.</p>
          <div className="mt-6"><Link to="/book-demo"><Button className="rounded-full px-5 h-11" data-testid="ai-cta-demo">Request live demo <ArrowRight className="size-4" /></Button></Link></div>
        </div>
      </section>

      <OrderAiSystemModal open={orderOpen} onOpenChange={setOrderOpen} system={activeSystem} />
    </div>
  );
}

function Info({ l, v }) {
  return (
    <div className="rounded-md bg-white border border-slate-200 p-2.5">
      <div className="text-[10px] uppercase tracking-widest text-slate-500">{l}</div>
      <div className="mt-0.5 text-sm font-medium text-slate-900">{v}</div>
    </div>
  );
}
